import { FC, useState } from "react";
import ModalDefault from "../../ModalDefault/ModalDefault";
import InputDropzone from "../../InputDropzone/InputDropzone";
import ModalArticlesReplaceFileBulkResult from "./ModalArticlesReplaceFileBulkResult";
import { ArticleBulkReplaceFileResult, useArticles } from "../../../providers/Articles/ArticlesProvider";
import FormatBytes from "../../../utils/FormatBytes";
import "./ModalArticlesReplaceFileBulk.scss";

interface ModalArticlesReplaceFileBulkProps {
  show: boolean;
  ids: number[];
  onClose: () => void;
  onDone: () => void;
}

// Mesmo ícone do botão "Upload local" dos Destaques do Chat (ChatHighlightForm.tsx).
const CloudUploadIcon: FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" aria-hidden="true">
    <path
      d="M8.36666 26.6666C6.41333 26.6666 4.74666 25.9799 3.37999 24.6133C2.01333 23.2466 1.32666 21.5866 1.32666 19.6266C1.32666 17.8933 1.87999 16.3733 2.99333 15.0599C4.10666 13.7466 5.51333 12.9599 7.22666 12.6933C7.67333 10.5399 8.71333 8.77327 10.36 7.4066C12.0067 6.03994 13.9067 5.35327 16.06 5.35327C18.5467 5.35327 20.6467 6.25994 22.36 8.07327C24.0733 9.8866 24.9267 12.0599 24.9267 14.5933V15.3933C26.5267 15.3466 27.88 15.8666 28.9933 16.9466C30.1067 18.0266 30.66 19.3866 30.66 21.0333C30.66 22.5666 30.1067 23.8866 28.9933 24.9999C27.88 26.1133 26.56 26.6666 25.0267 26.6666H16.9933V16.0666L19.06 18.1333C19.26 18.3333 19.4933 18.4333 19.76 18.4333C20.0267 18.4333 20.26 18.3333 20.46 18.1333C20.66 17.9333 20.76 17.6933 20.76 17.4133C20.76 17.1333 20.66 16.8933 20.46 16.6933L16.6933 12.8933C16.4933 12.6933 16.26 12.5933 15.9933 12.5933C15.7267 12.5933 15.4933 12.6933 15.2933 12.8933L11.4933 16.6933C11.2933 16.8933 11.2 17.1266 11.2067 17.3933C11.2133 17.6599 11.3267 17.8933 11.5267 18.0933C11.7267 18.2933 11.9667 18.3933 12.2467 18.3933C12.5267 18.3933 12.7667 18.2933 12.9667 18.0933L15 16.0599V26.6599H8.36666V26.6666Z"
      fill="currentColor"
    />
  </svg>
);

const FILE_TYPES = {
  "application/vnd.ms-excel": [".xlsx"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "text/csv": [".csv"],
};

const MAX_FILE_SIZE = 157286400;

const ModalArticlesReplaceFileBulk: FC<ModalArticlesReplaceFileBulkProps> = ({
  show,
  ids,
  onClose,
  onDone,
}) => {
  const [files, setFiles] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [result, setResult] = useState<ArticleBulkReplaceFileResult | null>(null);
  const { handleReplaceFileBulk } = useArticles();

  const handleClose = () => {
    setFiles([]);
    onClose();
  };

  const handleSubmit = async () => {
    if (files.length === 0 || ids.length === 0) return;

    setIsSubmitting(true);
    const response = await handleReplaceFileBulk(ids, files[0]);
    setIsSubmitting(false);

    if (!response) return;

    // Fecha o modal de upload antes de mostrar o resultado — senão ele fica visível
    // atrás, já que o resultado é outro modal por cima.
    handleClose();
    setResult(response);
  };

  const handleResultClose = () => {
    setResult(null);
    onDone();
  };

  return (
    <>
      <ModalDefault
        title="Substituir Anexos"
        show={show}
        onClose={handleClose}
        handleSubmit={handleSubmit}
        buttonText="Substituir"
        disabledSubmit={isSubmitting || files.length === 0}
        sizeModal="md"
      >
        <p>
          O arquivo selecionado abaixo vai substituir a planilha anexada no conteúdo (a que o
          usuário vê para download) de <b>{ids.length}</b> artigo(s) selecionado(s). Artigos
          travados por outro usuário, sem planilha no conteúdo ou com mais de uma planilha
          são pulados (não dá pra saber qual trocar).
        </p>
        <InputDropzone
          label="Planilha (.csv ou .xlsx)"
          name="replace_file"
          files={files}
          setFiles={setFiles}
          types={FILE_TYPES}
          maxFiles={1}
          maxSize={MAX_FILE_SIZE}
          icon={<CloudUploadIcon />}
          dropzoneTitle="Arraste ou selecione o arquivo aqui para fazer o upload."
          hint={<>Aceita arquivos <b>.xlsx</b> e <b>.csv</b> de até <b>{FormatBytes(MAX_FILE_SIZE)}</b>.</>}
          hideFileCountHint
          dropzoneClassName="dropzone-replace-file-bulk"
        />
      </ModalDefault>

      <ModalArticlesReplaceFileBulkResult
        show={!!result}
        result={result}
        onClose={handleResultClose}
      />
    </>
  );
};

export default ModalArticlesReplaceFileBulk;
